import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Carousel from '../components/ui/Carousel'
import ServiceCard from '../components/ui/ServiceCard'
import { Link } from 'react-router-dom'
import { FaArrowRight } from 'react-icons/fa'
import client from '../sanityClient'
import { homePageQuery } from '../queries'
import { getStaticHomePageData, getInitialHomePageData } from '../utils/staticDataLoader'

// import local images
import image1 from '../assets/images/1.jpg'
import image2 from '../assets/images/2.jpg'
import image3 from '../assets/images/3.jpg'

const HomeContainer = styled.div`
  width: 100%;
`;

const WelcomeSection = styled.section`
  padding: 4rem 0;
  background-color: ${({ theme }) => theme.colors.background};
`;

const WelcomeContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const WelcomeTitle = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const WelcomeText = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
  white-space: pre-line;
  p {
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const ServicesSection = styled.section`
  padding: 4rem 0;
  background-color: ${({ theme }) => theme.colors.lightBackground};
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 2.5rem;
  text-align: center;
`;

const ServicesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
`;

const CTASection = styled.section`
  padding: 4rem 0;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const CTATitle = styled.h2`
  color: white;
  margin-bottom: 1.5rem;
`;

const CTAText = styled.p`
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: white;
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.3s ease, transform 0.3s ease;
  
  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
    color: white;
    transform: translateY(-3px);
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const Home = () => {
  // Default content to show immediately (no loading state)
  const defaultContent = {
    title: 'Welcome to Tandara Lodge',
    introText: 'Providing a high standard of care and quality of life for those who choose to use our services. Our dedicated team ensures that residents receive compassionate, professional care in a warm and welcoming environment.'
  };

  const [pageData, setPageData] = useState(() => 
    getInitialHomePageData(defaultContent)
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        // Try to load static data first
        const staticData = await getStaticHomePageData();
        
        if (staticData && staticData.homePage) {
          setPageData(staticData.homePage);
        } else {
          // Fallback to runtime fetching
          const data = await client.fetch(homePageQuery);
          if (data) {
            setPageData(data);
          }
        }
      } catch (error) {
        console.error('Error loading home page data:', error);
        // Keep default content if all fails
      }
    };

    loadData();
  }, []);

  return (
    <HomeContainer>
      <Carousel />
      <WelcomeSection>
        <WelcomeContent>
          <WelcomeTitle>{pageData.title}</WelcomeTitle>
          <WelcomeText>
            <p>{pageData.introText}</p>
          </WelcomeText>
        </WelcomeContent>
      </WelcomeSection>

      
      <ServicesSection>
        <SectionTitle>Our Services</SectionTitle>
        <ServicesGrid>
          <ServiceCard 
            title="Residential Care"
            description="Our residential care provides a comfortable and caring environment for residents requiring ongoing support and assistance."
            image={image1}
            link="/services/residential"
          />
          <ServiceCard 
            title="Respite Care"
            description="Short-term care when you need it, providing a break for both you and your carers while ensuring you receive quality care."
            image={image2}
            link="/services/respite"
          />
          <ServiceCard 
            title="Independent Living"
            description="Affordable and comfortable accommodation in our independent living units at Sheffield and Railton."
            image={image3}
            link="/services/independent-living"
          />
        </ServicesGrid>
      </ServicesSection>
      
      <CTASection>
        <CTAContent>
          <CTATitle>Join Our Community</CTATitle>
          <CTAText>
            We welcome new members to join our Association. Learn more about our services, 
            facilities, and how we can provide the care you or your loved ones need.
          </CTAText>
          <CTAButton to="/contact">
            Contact Us <FaArrowRight />
          </CTAButton>
        </CTAContent>
      </CTASection>
    </HomeContainer>
  );
};

export default Home;
